import {css, view} from '@sail/ui/deprecated';
import {
  Conversation,
  ConversationMessageAdded,
} from '@stripe-internal/conversations-client';
import {useEffect, useRef} from 'react';
import {reverse} from 'lodash';
import {debounceTime, filter} from 'rxjs';
import {LogItems} from './LogItems';
import {TypingIndicator} from '../TypingIndicator';
import {LoadingIndicator} from '../LoadingIndicator';
import {MessageScroller} from '../MessageScroller';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../../FeatureFlagsProvider';
import {
  TopAnchoringConfigProvider,
  useMessagesTopAnchored,
} from '../../helpers/useMessagesTopAnchored';
import {LoadingTextProvider} from '../LoadingText';
import {TypingIndicatorNext} from '../TypingIndicatorNext';
import {DotComLoadingIndicator} from '../DotComLoadingIndicator';
import {DotComLogItems} from './DotComLogItems';
import {useWidgetVisibility} from '../../Widget/useWidget';
import {useConversationLogItems} from '../useConversationLogItems';

type Props = {
  conversation: Conversation;
  animated?: boolean;
  disableInteraction?: boolean;
};

const supportsReverseColumn = () =>
  window?.CSS?.supports &&
  typeof window.CSS.supports === 'function' &&
  window.CSS.supports('overflow-anchor: auto');

const SCROLL_DELAY_MS = 16;

const scrollToAnchor = (anchor?: HTMLDivElement | null) => {
  setTimeout(() => {
    requestAnimationFrame(() => {
      anchor?.scrollIntoView({behavior: 'smooth'});
    });
  }, SCROLL_DELAY_MS);
};

function useEnsureIncomingMessagesInLog(conversation: Conversation) {
  useEffect(() => {
    const sub = conversation
      .eventsObservable()
      .pipe(
        // when we get new messages from someone else
        filter(
          (e) =>
            e.name === 'conversation-message-added' &&
            e.message?.participant.role === 'agent',
        ),
        // wait a bit to make sure the message are added to log
        debounceTime(2000),
      )
      .subscribe((e) => {
        const msgAddedEvent = e as ConversationMessageAdded;
        const rawLog = conversation.log.getUnsorted();
        const id = msgAddedEvent.nonce ?? msgAddedEvent.messageId;
        const hasItem = !!rawLog[id];
        if (!hasItem) {
          const agentMessageCount = conversation.log
            .get()
            .filter(
              (item) =>
                item.name === 'conversation-message-added' &&
                item.message.participant.role === 'agent',
            ).length;
          const conversationHasMessageById = !!conversation.getMessage(
            msgAddedEvent.messageId,
          );
          const conversationHasMessageByNonce = !!(
            msgAddedEvent.nonce && conversation.getMessage(msgAddedEvent.nonce)
          );
          const logHasMessageById = !!rawLog[msgAddedEvent.messageId];
          const logHasMessageByNonce = !!(
            msgAddedEvent.nonce && rawLog[msgAddedEvent.nonce]
          );
          const tags = {
            conversationId: conversation.id,
            messageId: id,
            agentMessageCount: agentMessageCount.toString(),
            conversationHasMessageById: conversationHasMessageById.toString(),
            conversationHasMessageByNonce:
              conversationHasMessageByNonce.toString(),
            logHasMessageById: logHasMessageById.toString(),
            logHasMessageByNonce: logHasMessageByNonce.toString(),
          };
          conversation.client.errors.reportWarning(
            '[Log] appears message received may not be in log',
            {tags},
          );
        }
      });

    return () => sub.unsubscribe();
  }, [conversation]);
}

export const Log = ({conversation, animated, disableInteraction}: Props) => {
  const useNewDesign = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();
  const {hasTopAnchorFlag, shouldTopAnchor} =
    useMessagesTopAnchored(conversation);
  const {visibility} = useWidgetVisibility();

  const items = useConversationLogItems(conversation);
  const logRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEnsureIncomingMessagesInLog(conversation);

  useEffect(() => {
    if (supportsReverseColumn()) {
      return;
    }

    // only auto-scrolling using the anchor if the browser does not support
    // column-reverse auto-scrolling.
    // this won't respect the user's scroll position if they've scrolled up
    scrollToAnchor(anchorRef.current);
  }, [items]);

  const handleScroll = () => {
    const logContainer = logRef.current;

    // Due to certain screen resolutions and zoom settings, the `scrollTop` field on the Log container
    // can sometimes get set to a value that's between 0 and 1, which breaks auto-scrolling.
    //
    // Since we're using `flex-direction: column-reverse` to enable auto-scrolling, this will only work
    // if `scrollTop` is set to 0. To fix this, we're setting it to 0 if `scrollTop` ever gets into a weird state.
    if (
      logContainer &&
      logContainer.scrollTop > 0 &&
      logContainer.scrollTop <= 1
    ) {
      logContainer.scrollTop = 0;
    }
  };

  // default is column-reverse, so rendering these in reverse order by default as well
  const logComponents = [];

  if (useNewDesign) {
    if (hasTopAnchorFlag) {
      if (supportsReverseColumn()) {
        logComponents.push(
          <MessageScroller
            key="message-scroller"
            conversation={conversation}
            logRef={logRef}
            contentRef={contentRef}
            isOpen={visibility === 'open'}
          />,
        );
      }

      logComponents.push(
        <view.div
          key="anchor"
          ref={anchorRef}
          css={{
            flexGrow: css.value('1'),
            flexShrink: css.value('1'),
          }}
        />,
      );
    } else {
      logComponents.push(
        <LoadingIndicator
          key="loading-indicator"
          conversation={conversation}
        />,
      );

      logComponents.push(
        <TypingIndicatorNext
          key="typing-indicator"
          conversation={conversation}
          animated={animated}
        />,
      );
    }
  } else {
    logComponents.push(
      <view.div
        key="anchor"
        ref={anchorRef}
        css={{
          flexGrow: css.value('1'),
          flexShrink: css.value('1'),
        }}
      />,
    );

    logComponents.push(
      <view.div
        key="typing-indicator"
        css={{
          minHeight: useDotComDesign ? undefined : css.value('32px'),
          '&:empty': {display: css.value('none')},
        }}
      >
        {useDotComDesign ? (
          <DotComLoadingIndicator conversation={conversation} />
        ) : (
          <TypingIndicator conversation={conversation} animated={animated} />
        )}
      </view.div>,
    );
  }

  logComponents.push(
    useDotComDesign ? (
      <DotComLogItems
        key="log-items"
        ref={contentRef}
        conversation={conversation}
        items={items}
        animated={animated}
        disableInteraction={disableInteraction}
      />
    ) : (
      <LogItems
        key="log-items"
        ref={contentRef}
        conversation={conversation}
        items={items}
        animated={animated}
        disableInteraction={disableInteraction}
      />
    ),
  );

  // column-reverse gives us magical auto-scrolling
  let flexDirection: 'column-reverse' | 'column' = 'column-reverse';
  let children = logComponents;
  if (!supportsReverseColumn()) {
    flexDirection = 'column';
    children = reverse(logComponents);
  }

  return (
    <TopAnchoringConfigProvider>
      <LoadingTextProvider>
        <view.div
          css={{flexGrow: css.value('1'), position: css.value('relative')}}
        >
          <view.div
            ref={logRef}
            onScroll={handleScroll}
            css={{
              display: css.value('flex'),
              flexDirection: css.value(flexDirection),
              overflowY: css.value('auto'),
              ...(useDotComDesign ? {scrollbarWidth: css.value('none')} : {}),

              ...(hasTopAnchorFlag
                ? {overscrollBehavior: css.value('contain')}
                : {}),

              ...(shouldTopAnchor ? {overflowAnchor: css.value('none')} : {}),

              position: css.value('absolute'),
              top: 0,
              bottom: 0,
              right: 0,
              left: 0,
              padding: 'medium',
              paddingBottom: useNewDesign ? 'medium' : 0,
              // eslint-disable-next-line no-nested-ternary
              gap: hasTopAnchorFlag
                ? useDotComDesign
                  ? 'medium'
                  : undefined
                : useNewDesign
                ? 'large'
                : 'medium',
            }}
          >
            {children}
          </view.div>
        </view.div>
      </LoadingTextProvider>
    </TopAnchoringConfigProvider>
  );
};
